import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
	BrowserRouter as Router,
	Route,
	Routes,
	Navigate,
	useNavigate,
	Link,
	useLocation,
} from 'react-router-dom';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Add compatibility helper for requesting password reset (v2 and v1)
async function requestPasswordReset(email) {
	// v2
	if (typeof supabase?.auth?.resetPasswordForEmail === 'function') {
		return supabase.auth.resetPasswordForEmail(email, {
			redirectTo: `${window.location.origin}/change-password`,
		});
	}
	// v1 fallback
	if (typeof supabase?.auth?.api?.resetPasswordForEmail === 'function') {
		return supabase.auth.api.resetPasswordForEmail(email, {
			redirectTo: `${window.location.origin}/change-password`,
		});
	}
	// None available — helpful error for developer
	throw new Error(
		'No resetPasswordForEmail function found on supabase client. Upgrade @supabase/supabase-js or call the REST endpoint server-side.'
	);
}

// Forgot Password Form
function ForgotPasswordForm() {
	const [email, setEmail] = useState('');
	const [message, setMessage] = useState('');
	// Turnstile removed; no additional state required

	// Render Turnstile widget after mount
	// No Turnstile hook

	const handleForgotPassword = async (e) => {
		e.preventDefault();
		// show non-revealing message to user
		setMessage('If an account exists for this email, a reset link has been sent.');
		if (!email) {
			setMessage('Please enter your email.');
			return;
		}
		try {
			// Turnstile removed — directly request reset email

			// Request a reset email via compatibility helper (v2 or v1)
			const { error } = await requestPasswordReset(email);

			if (error) throw error;

			// don't reveal account existence — message already set
		} catch (error) {
			// preserve non-revealing messaging; but surface to dev console
			console.error('resetPasswordForEmail error', error);
		}
	};

	return (
		<div style={styles.container}>
			<h2 style={styles.title}>Forgot Password</h2>
			<form onSubmit={handleForgotPassword} style={styles.form}>
				<input
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder="Email"
					style={styles.input}
					required
				/>
				<button
					type="submit"
					style={styles.button}
				>
					Send Reset Email
				</button>
			</form>
			{message && <p style={styles.message}>{message}</p>}
		</div>
	);
}

// Change Password Form
function ChangePasswordForm() {
	const [email, setEmail] = useState('');
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [message, setMessage] = useState('');
	const [isResetFlow, setIsResetFlow] = useState(false);
	const [passwordWarning, setPasswordWarning] = useState('');
	const [matchWarning, setMatchWarning] = useState('');
	// Turnstile removed

	const navigate = useNavigate();
	// useTurnstileScript();

	// Strong password validation
	function isStrongPassword(pw) {
		return (
			pw.length >= 8 &&
			/[a-z]/.test(pw) &&
			/[A-Z]/.test(pw) &&
			/[0-9]/.test(pw) &&
			/[^A-Za-z0-9]/.test(pw)
		);
	}

	useEffect(() => {
		// Use URL hash to detect Supabase password reset flow and store session
		const checkReset = async () => {
			const hash = window.location.hash;
			if (hash && hash.includes('access_token')) {
				setIsResetFlow(true);
				// parse session from url and store it in client (v2 helper)
				try {
					await supabase.auth.getSessionFromUrl({ storeSession: true });
				} catch (err) {
					// ignore; we'll detect session later
					console.error('getSessionFromUrl error', err);
				}
			} else {
				setIsResetFlow(false);
			}
		};
		checkReset();
	}, []);

	useEffect(() => {
		if (newPassword && !isStrongPassword(newPassword)) {
			setPasswordWarning(
				'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.'
			);
		} else {
			setPasswordWarning('');
		}
	}, [newPassword]);

	useEffect(() => {
		if (newPassword && confirmPassword && newPassword !== confirmPassword) {
			setMatchWarning('Passwords do not match.');
		} else {
			setMatchWarning('');
		}
	}, [newPassword, confirmPassword]);

	// No Turnstile rendering

	const handleChangePassword = async (e) => {
		e.preventDefault();
		setMessage('');
		// Turnstile removed — proceed without challenge
		if (!newPassword) {
			setMessage('Please enter a new password.');
			return;
		}
		if (!isStrongPassword(newPassword)) {
			setMessage(
				'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.'
			);
			return;
		}
		if (newPassword !== confirmPassword) {
			setMessage('New passwords do not match.');
			return;
		}
		try {
			// Turnstile removed — directly proceed

			if (isResetFlow) {
				// v2: get stored session and update password for current session
				const { data: sessionData } = await supabase.auth.getSession();
				const session = sessionData?.session;
				if (!session?.access_token) {
					setMessage('Invalid or expired reset link.');
					return;
				}
				const { error } = await supabase.auth.updateUser({ password: newPassword });
				if (error) throw error;
				setMessage('Password has been reset successfully. You will be logged out.');
				await supabase.auth.signOut();
				setTimeout(() => navigate('/forgot-password'), 3000);
			} else {
				// v2 sign in and update user using current session
				if (!email) {
					setMessage('Please enter your email address.');
					return;
				}
				if (!currentPassword) {
					setMessage('Please enter your current password.');
					return;
				}
				const { error: signInError } = await supabase.auth.signInWithPassword({
					email,
					password: currentPassword,
				});
				if (signInError) throw signInError;

				const { error } = await supabase.auth.updateUser({ password: newPassword });
				if (error) throw error;
				setMessage('Password changed successfully. You will be logged out.');
				await supabase.auth.signOut();
				setTimeout(() => navigate('/forgot-password'), 3000);
			}
		} catch (error) {
			setMessage(`Error: ${error.message}`);
		}
	};

	return (
		<div style={styles.container}>
			<h2 style={styles.title}>Change Password</h2>
			<form onSubmit={handleChangePassword} style={styles.form}>
				{!isResetFlow && (
					<>
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="Email"
							style={styles.input}
							required
						/>
						<input
							type="password"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							placeholder="Current Password"
							style={styles.input}
							required
						/>
					</>
				)}
				<input
					type="password"
					value={newPassword}
					onChange={(e) => setNewPassword(e.target.value)}
					placeholder="New Password"
					style={styles.input}
					required
				/>
				{passwordWarning && (
					<div style={{ color: 'orange', fontSize: '13px', marginBottom: '8px' }}>
						{passwordWarning}
					</div>
				)}
				<input
					type="password"
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					placeholder="Confirm New Password"
					style={styles.input}
					required
				/>
				{matchWarning && (
					<div style={{ color: 'orange', fontSize: '13px', marginBottom: '8px' }}>
						{matchWarning}
					</div>
				)}
				<button
					type="submit"
					style={styles.button}
				>
					Change Password
				</button>
			</form>
			{message && <p style={styles.message}>{message}</p>}
		</div>
	);
}

const styles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'center',
		minHeight: '100vh',
		backgroundColor: '#ffffff',
		padding: '32px 8px 8px 8px',
	},
	title: {
		fontSize: '22px',
		fontWeight: 'bold',
		marginBottom: '16px',
		color: '#333333',
		marginTop: '32px',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		maxWidth: '340px',
		minWidth: '0',
		background: '#fafafa',
		border: '1px solid #e0e0e0',
		borderRadius: '8px',
		padding: '18px 16px 16px 16px',
		boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
	},
	input: {
		marginBottom: '10px',
		padding: '10px',
		fontSize: '16px',
		border: '1px solid #00a884',
		borderRadius: '4px',
		width: '100%',
		boxSizing: 'border-box',
	},
	button: {
		padding: '10px',
		fontSize: '16px',
		backgroundColor: '#00a884',
		color: '#ffffff',
		border: 'none',
		borderRadius: '4px',
		cursor: 'pointer',
		marginTop: '4px',
	},
	message: {
		marginTop: '10px',
		color: 'red',
		textAlign: 'center',
		wordBreak: 'break-word',
	},
};

// Navigation menu component
function NavMenu() {
	const location = useLocation();
	return (
		<div style={navStyles.menu}>
			<Link
				to="/forgot-password"
				style={{
					...navStyles.link,
					fontWeight: location.pathname === '/forgot-password' ? 'bold' : 'normal',
				}}
			>
				Forgot Password
			</Link>
			<span style={navStyles.separator}>|</span>
			<Link
				to="/change-password"
				style={{
					...navStyles.link,
					fontWeight: location.pathname === '/change-password' ? 'bold' : 'normal',
				}}
			>
				Change Password
			</Link>
		</div>
	);
}

const navStyles = {
	menu: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginTop: '30px',
		marginBottom: '10px',
		gap: '10px',
	},
	link: {
		color: '#00a884',
		textDecoration: 'none',
		fontSize: '16px',
		padding: '0 8px',
	},
	separator: {
		color: '#888',
		fontSize: '16px',
	},
};

export default function AppWrapper() {
	React.useEffect(() => {
		document.title = "UltimateAI";
	}, []);
	return (
		<Router>
			<NavMenu />
			<Routes>
				<Route path="/forgot-password" element={<ForgotPasswordForm />} />
				<Route path="/change-password" element={<ChangePasswordForm />} />
				<Route path="*" element={<Navigate to="/forgot-password" />} />
			</Routes>
		</Router>
	);
}